import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Paper,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import {
  masterHardwareCatalogList,
  masterHardwareCatalogCreate,
  masterHardwareCatalogUpdate,
  masterHardwareCatalogDelete,
} from "../../../../store/actions";
import SettingsPanel from "../../components/SettingsPanel";

const Master = () => {
  const dispatch = useDispatch();

  // 1. Fetch State from Redux
  const {
    masterHardwareCatalogList: catalogData,
    masterHardwareCatalogListLoading: isLoading,
  } = useSelector((state) => state.masterReducer);

  // Safely destructure the array from the API response
  const catalogList = catalogData?.masterhardwarecatalog_list || [];

  // DataGrid requires a unique 'id' property. We map the uppercase 'Id' to lowercase 'id'.
  const rows = catalogList.map((item) => ({
    ...item,
    id: item.Id,
  }));

  // 2. Local State
  const initialFormState = {
    Name: "",
    Manufacturer: "",
    Model: "",
    Notes: "",
    FlagPrinter: "",
    FlagCardTerminal: "",
    Description: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // 3. Fetch Data on Mount
  useEffect(() => {
    dispatch(masterHardwareCatalogList());
  }, [dispatch]);

  // 4. Handlers
  const handleOpenDialog = (item = null) => {
    if (item && item.Id) {
      setIsEditing(true);
      setEditId(item.Id);
      setFormData({
        Name: item.Name || "",
        Manufacturer: item.Manufacturer || "",
        Model: item.Model || "",
        Notes: item.Notes || "",
        FlagPrinter: item.FlagPrinter || "",
        FlagCardTerminal: item.FlagCardTerminal || "",
        Description: item.Description || "",
      });
    } else {
      setIsEditing(false);
      setEditId(null);
      setFormData(initialFormState);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData(initialFormState);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEditing) {
      const updatePayload = [{ Id: editId, ...formData }];
      dispatch(masterHardwareCatalogUpdate(updatePayload));
    } else {
      const createPayload = [{ ...formData }];
      dispatch(masterHardwareCatalogCreate(createPayload));
    }

    handleCloseDialog();
    setTimeout(() => dispatch(masterHardwareCatalogList()), 500);
  };

  const handleDeleteClick = (id) => {
    if (window.confirm("Are you sure you want to delete this hardware item?")) {
      dispatch(masterHardwareCatalogDelete([id]));
      setTimeout(() => dispatch(masterHardwareCatalogList()), 500);
    }
  };

  // 5. DataGrid Columns
  const columns = [
    { field: "Id", headerName: "ID", width: 90 },
    { field: "Manufacturer", headerName: "Manufacturer", flex: 1 },
    { field: "Model", headerName: "Model", flex: 1 },
    { field: "FlagCardTerminal", headerName: "Card Terminal Flag", width: 150 },
    { field: "Notes", headerName: "Notes", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="primary"
            onClick={() => handleOpenDialog(params.row)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDeleteClick(params.row.Id)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Master"
      description="Master hardware catalog of printers and card terminals."
    >
      <Box sx={{ p: 3 }}>
        {/* HEADER SECTION */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Master Hardware Catalog
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Hardware
        </Button>
      </Box>

      {/* DATA GRID SECTION */}
      <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* CREATE / EDIT DIALOG */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? "Edit Hardware" : "Add New Hardware"}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <TextField
              margin="dense"
              label="Manufacturer"
              name="Manufacturer"
              value={formData.Manufacturer}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Model"
              name="Model"
              value={formData.Model}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              margin="dense"
              label="Name"
              name="Name"
              value={formData.Name}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Card Terminal Flag (e.g., 1)"
              name="FlagCardTerminal"
              value={formData.FlagCardTerminal}
              onChange={handleInputChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Notes"
              name="Notes"
              value={formData.Notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {isEditing ? "Save Changes" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      </Box>
    </SettingsPanel>
  );
};

export default Master;
